
import React from 'react';
import LatexRenderer from './LatexRenderer';

interface ProblemBodyRendererProps {
  lines: string[];
  figureImage?: string;
}

const ContentWithImageBottom: React.FC<{ text: string; figureImage?: string; preserveLinebreaks: boolean }> = ({ text, figureImage, preserveLinebreaks }) => {
  // Strip [FIGURE] placeholder as we will render the image explicitly at the bottom
  const cleanText = text.replace(/\[FIGURE\]/g, '').trim();

  return (
    <div className="w-full">
        {cleanText && (
            <div className="mb-4">
                <LatexRenderer latex={cleanText} preserveLinebreaks={preserveLinebreaks} disableAutoAlign={true} />
            </div>
        )}
        
        {figureImage && (
            <div className="w-full flex justify-center mt-4 mb-2">
                <img 
                    src={`data:image/jpeg;base64,${figureImage}`} 
                    alt="Problem figure" 
                    className="w-[70%] h-auto rounded-md border border-[var(--color-border-glass)] shadow-sm object-contain mx-auto block" 
                />
            </div>
        )}
    </div>
  );
};

const ProblemBodyRenderer: React.FC<ProblemBodyRendererProps> = ({ lines, figureImage }) => {
  if (!lines || lines.length === 0) {
    return null;
  }
  const fullText = lines.join('\n');
  
  // Common phrases that often appear after a boxed section.
  const afterPhrases = [
    '옳은 것만을 있는 대로 고른 것은?',
    '옳은 것만을 있는대로 고른 것은?', // without space
    '옳은 것을 모두 고른 것은?',
    '옳은 것은?',
    '참인 명제만을 있는 대로 고른 것은?',
    '참인 명제를 모두 고른 것은?',
  ];

  // --- 0. Check for explicit BOX_START / BOX_END markers ---
  const boxStartIndex = fullText.indexOf('BOX_START');
  const boxEndIndex = fullText.indexOf('BOX_END');

  if (boxStartIndex !== -1 && boxEndIndex !== -1 && boxEndIndex > boxStartIndex) {
      const beforeBox = fullText.substring(0, boxStartIndex);
      const boxContent = fullText.substring(boxStartIndex + 'BOX_START'.length, boxEndIndex);
      const afterBox = fullText.substring(boxEndIndex + 'BOX_END'.length);

      return (
        <>
            {beforeBox.trim() && <LatexRenderer latex={beforeBox} preserveLinebreaks={false} disableAutoAlign={true} />}
            
            <div className="problem-body-renderer-box">
                <LatexRenderer latex={boxContent} preserveLinebreaks={true} disableAutoAlign={true} />
            </div>

            {/* Image placed between box and final question */}
            {figureImage && (
                <div className="w-full flex justify-center mt-4 mb-4">
                    <img 
                        src={`data:image/jpeg;base64,${figureImage}`} 
                        alt="Problem figure" 
                        className="w-[70%] h-auto rounded-md border border-[var(--color-border-glass)] shadow-sm object-contain mx-auto block" 
                    />
                </div>
            )}

            {afterBox.trim() && <LatexRenderer latex={afterBox} preserveLinebreaks={false} disableAutoAlign={true} />}
        </>
      );
  }

  // --- 1. Check for <보기> block ---
  const bogiMarker = '<보기>';
  // Use lastIndexOf to be more robust against OCR errors like "<보기>에서 ... <보기> ㄱ."
  const bogiIndex = fullText.lastIndexOf(bogiMarker);

  if (bogiIndex !== -1) {
    const beforeBogi = fullText.substring(0, bogiIndex);
    const contentAfterBogi = fullText.substring(bogiIndex);
    
    let bogiBoxContent = contentAfterBogi;
    let afterBogiBoxContent = '';

    for (const phrase of afterPhrases) {
      // Find the phrase *after* the bogi marker itself to avoid splitting prematurely
      const phraseIndex = contentAfterBogi.indexOf(phrase, bogiMarker.length);
      if (phraseIndex !== -1) { 
        bogiBoxContent = contentAfterBogi.substring(0, phraseIndex);
        afterBogiBoxContent = contentAfterBogi.substring(phraseIndex);
        break;
      }
    }

    return (
      <>
        {beforeBogi.trim() && <LatexRenderer latex={beforeBogi} preserveLinebreaks={false} disableAutoAlign={true} />}
        
        <div className="problem-body-renderer-box">
          <LatexRenderer latex={bogiBoxContent} preserveLinebreaks={true} disableAutoAlign={true} />
        </div>
        
        {/* Image placed between 보기 and final question with 70% width */}
        {figureImage && (
            <div className="w-full flex justify-center mt-4 mb-4">
                <img 
                    src={`data:image/jpeg;base64,${figureImage}`} 
                    alt="Problem figure" 
                    className="w-[70%] h-auto rounded-md border border-[var(--color-border-glass)] shadow-sm object-contain mx-auto block" 
                />
            </div>
        )}

        {afterBogiBoxContent && <LatexRenderer latex={afterBogiBoxContent} preserveLinebreaks={false} disableAutoAlign={true} />}
      </>
    );
  }

  // --- 2. If no <보기>, check for (가), (나) conditions ---
  const conditions = ['(가)', '(나)', '(다)', '(라)'];
  let firstConditionIndex = -1;

  for (const cond of conditions) {
      const index = fullText.indexOf(cond);
      if (index !== -1) {
          if (firstConditionIndex === -1 || index < firstConditionIndex) {
              firstConditionIndex = index;
          }
      }
  }

  if (firstConditionIndex !== -1) {
    const textBeforeConditions = fullText.substring(0, firstConditionIndex);
    const contentWithConditionsAndQuestion = fullText.substring(firstConditionIndex);

    let conditionsBoxContent = contentWithConditionsAndQuestion;
    let afterConditionsBoxContent = '';

    const lines = contentWithConditionsAndQuestion.split('\n');
    let lastConditionLineIndex = -1;
    
    // Iterate backwards to find the last line that starts with a condition marker
    for (let i = lines.length - 1; i >= 0; i--) {
        if (conditions.some(cond => lines[i].trim().startsWith(cond))) {
            lastConditionLineIndex = i;
            break;
        }
    }

    if (lastConditionLineIndex !== -1) {
        // Everything up to and including the last condition line goes in the box.
        conditionsBoxContent = lines.slice(0, lastConditionLineIndex + 1).join('\n');
        // Everything after goes outside the box.
        afterConditionsBoxContent = lines.slice(lastConditionLineIndex + 1).join('\n');
    }

    return (
        <>
            {textBeforeConditions.trim() && <LatexRenderer latex={textBeforeConditions} preserveLinebreaks={false} disableAutoAlign={true} />}
            
            <div className="problem-body-renderer-box">
                <LatexRenderer latex={conditionsBoxContent} preserveLinebreaks={true} disableAutoAlign={true} />
            </div>

             {/* Image placed between conditions and final question with 70% width */}
             {figureImage && (
                <div className="w-full flex justify-center mt-4 mb-4">
                    <img 
                        src={`data:image/jpeg;base64,${figureImage}`} 
                        alt="Problem figure" 
                        className="w-[70%] h-auto rounded-md border border-[var(--color-border-glass)] shadow-sm object-contain mx-auto block" 
                    />
                </div>
            )}

            {afterConditionsBoxContent.trim() && <LatexRenderer latex={afterConditionsBoxContent.trim()} preserveLinebreaks={false} disableAutoAlign={true} />}
        </>
    );
  }


  // --- 3. If no special blocks, render the whole text with image at bottom ---
  return <ContentWithImageBottom text={fullText} figureImage={figureImage} preserveLinebreaks={false} />;
};

export default React.memo(ProblemBodyRenderer);
